// Card kết quả Xem ngày tốt — render bảng tất định từ BE (trực, sao hoàng đạo,
// giờ hoàng đạo, xung hợp tuổi) + danh sách ngày lân cận điểm cao
// + dải 3 phương án ngày đề xuất (v72).
import { CalendarCheck, Clock3, Medal } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { NgayTotReport } from "@/lib/boide-api";

const TUOI_LABELS: Record<string, string> = {
  xung: "Xung tuổi",
  luc_hop: "Lục hợp tuổi",
  tam_hop: "Tam hợp tuổi",
  hai: "Lục hại tuổi",
  binh: "Bình hòa với tuổi",
};

function scoreColor(score: number): string {
  if (score >= 65) return "text-emerald-400";
  if (score >= 45) return "text-[#E5B869]";
  return "text-red-400";
}

export function NgayTotCard({
  report,
  alternatives,
}: {
  report: NgayTotReport;
  alternatives: NgayTotReport[];
}) {
  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarCheck className="h-4 w-4 text-primary" /> Bảng ngày {report.display}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-2xl font-bold ${scoreColor(report.score)}`}>
            {report.score}/100
          </span>
          <Badge className="bg-gold-gradient/15 text-foreground">{report.verdict}</Badge>
          <Badge
            className={
              report.is_hoang_dao
                ? "bg-emerald-500/15 text-emerald-300"
                : "bg-red-500/15 text-red-300"
            }
          >
            {report.is_hoang_dao ? "Hoàng đạo" : "Hắc đạo"} · {report.sao.name}
          </Badge>
          {(report.tuoi_list?.length ? report.tuoi_list : report.tuoi ? [report.tuoi] : []).map(
            (t, i) => (
              <Badge
                key={i}
                className={
                  t.kind === "xung" || t.kind === "hai"
                    ? "bg-red-500/15 text-red-300"
                    : "bg-gold-gradient/15 text-foreground"
                }
              >
                {TUOI_LABELS[t.kind] ?? t.kind} {t.chi_tuoi}
                {"birth_year" in t && t.birth_year ? ` (${t.birth_year})` : ""}
              </Badge>
            ),
          )}
          {report.purpose ? (
            <Badge
              className={
                report.purpose.delta >= 0
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-red-500/15 text-red-300"
              }
            >
              {report.purpose.label}: {report.purpose.delta >= 0 ? "+" : ""}
              {report.purpose.delta} điểm
            </Badge>
          ) : null}
        </div>
        <div className="grid gap-1 text-xs text-muted-foreground">
          <div>
            Âm lịch {report.lunar_display} · ngày {report.can_chi_ngay} · tiết {report.tiet_khi}
          </div>
          <div>
            <span className="text-foreground">Trực {report.truc.name}</span> — {report.truc.y_nghia}{" "}
            Tốt cho: {report.truc.tot_cho}. Kỵ: {report.truc.xau_cho}.
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Clock3 className="h-3 w-3 text-primary" /> Giờ hoàng đạo:
            {report.gio_hoang_dao.map((g) => (
              <span
                key={g.gio_chi}
                className="rounded-full border border-gold-soft/40 bg-background/40 px-2 py-0.5"
              >
                {g.gio_chi} {g.khung_gio}
              </span>
            ))}
          </div>
        </div>
        {alternatives.length > 0 ? (
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">
              Ngày lân cận điểm cao (tham khảo đổi ngày):
            </div>
            <ul className="space-y-1">
              {alternatives.map((alt) => (
                <li
                  key={alt.solar_date}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border/40 bg-background/30 px-2.5 py-1.5 text-xs"
                >
                  <span>
                    {alt.display}{" "}
                    <span className="text-muted-foreground">(ÂL {alt.lunar_display})</span>
                    {" · "}trực {alt.truc.name} · {alt.sao.name}
                  </span>
                  <span className={`font-semibold ${scoreColor(alt.score)}`}>{alt.score}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ─── 3 phương án ngày đề xuất (v72, thẻ có config.suggest_options) ───────────

const OPTION_ACCENT: Record<string, string> = {
  tot_nhat: "border-[#E5B869]/60 bg-[#E5B869]/8",
  can_bang: "border-emerald-500/40 bg-emerald-500/5",
  du_phong: "border-border/50 bg-background/30",
};

export function NgayOptionsCards({ options }: { options: NgayTotReport[] }) {
  if (!options.length) return null;
  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Medal className="h-4 w-4 text-primary" /> 3 phương án ngày đề xuất
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-3">
          {options.map((opt) => (
            <div
              key={opt.solar_date}
              className={`space-y-1.5 rounded-xl border p-3 ${
                OPTION_ACCENT[opt.option_key ?? ""] ?? OPTION_ACCENT.du_phong
              }`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {opt.option_label ?? "Phương án"}
              </div>
              <div className="text-sm font-semibold text-foreground">{opt.display}</div>
              <div className="text-xs text-muted-foreground">ÂL {opt.lunar_display}</div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className={`text-lg font-bold ${scoreColor(opt.score)}`}>
                  {opt.score}
                </span>
                <Badge className="bg-gold-gradient/15 text-[10px] text-foreground">
                  {opt.verdict}
                </Badge>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Trực {opt.truc.name} · sao {opt.sao.name}
              </div>
              {opt.gio_hoang_dao.length ? (
                <div className="text-[11px] text-muted-foreground">
                  Giờ đẹp:{" "}
                  {opt.gio_hoang_dao
                    .slice(0, 3)
                    .map((g) => `${g.gio_chi} (${g.khung_gio})`)
                    .join(", ")}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
